<template>
	<div class="header_profitability_analysis is-full-width px-5 py-2">
		<p class="title">Análisis de Categorías</p>

		<div v-if="categories.length > 0" class="category_filters is-flex is-align-items-center">
			<SelectComponent
				v-for="cat in categories"
				:key="cat.id"
				class="category_select"
				:options="cat.options"
				:staticLabel="cat.label"
				:initialSelectedValues="selectedItems[cat.id] || []"
				iconRight="ArrowDown"
				theme="classification_theme"
				:isSearchable="true"
				@dropdown-open="onCategoryOpen(cat)"
				@selected-values="(values) => onCategoryChange(cat.id, values)" />
		</div>

		<div class="right_cluster is-flex is-align-items-center">
			<div v-if="endMonth" class="calendar_picker is-flex is-align-items-center">
				<AnualCalendar
					:value="endMonth"
					:disableNewerDates="true"
					:disabledFrom="getNow"
					formatLabelDate="MMMM YYYY"
					:alignRight="true"
					@date-change="onDateChange" />
			</div>

			<button class="export_btn ml-3 is-flex is-align-items-center is-disabled-local"
				disabled title="Próximamente">
				<DownloadFile class="mr-2 export_icon" />
				Exportar
			</button>

			<button class="close_btn ml-3" aria-label="Cerrar modal" @click="$emit('close-modal')">
				<Ecs class="ecs" />
			</button>
		</div>
	</div>
</template>

<script>
import { mapState } from "vuex";
import Ecs from "@/components/svg/Ecs.vue";
import DownloadFile from "@/components/svg/DownloadFile.vue";
import SelectComponent from "@/components/classification/SelectComponent.vue";
import AnualCalendar from "@/components/classification/AnualCalendar.vue";

// Header del drawer "Análisis de Categorías" (Rentabilidad). Mismo cluster que
// el de Costos: título, un select por categoría configurada, mes, Exportar y
// cerrar. Diferencia propia: el select con el que se interactúa pasa a ser la
// categoría PRINCIPAL del análisis (la de las cards y la evolución), no sólo
// un filtro de ítems — así se pasa de Tiendas a Marcas sin cerrar el drawer.
// La principal se muestra con todos sus ítems tildados (el componente pinta
// el trigger activo); las demás, vacías. Basta ABRIR el select de otra para
// que pase a ser la principal (`dropdown-open`), así el desplegable se ve ya
// con todo tildado.
export default {
	name: "HeaderProfitabilityAnalysis",
	components: { Ecs, DownloadFile, SelectComponent, AnualCalendar },
	props: {
		// Shape esperado por ModalFullHeightRight.headerProps:
		// - periodLabel: String — sólo se usa si endMonth no viene
		// - endMonth: Date — mes focal del drawer; alimenta AnualCalendar
		// - getNow: Date — cota superior (mes actual) para deshabilitar futuro
		// - categories: Array<{ id, label, options: [{value, label}] }>
		payload: {
			type: Object,
			default: () => ({
				periodLabel: "",
				endMonth: null,
				getNow: null,
				categories: [],
			}),
		},
	},
	computed: {
		// Del store para que el header reaccione en vivo. `selectedItems` trae
		// todos los ítems de la categoría principal, así su select se pinta
		// activo (el propio componente lo hace al haber ítems tildados) y las
		// otras categorías quedan en claro.
		...mapState("profitabilityAnalysis", ["category", "selectedItems"]),
		periodLabel() {
			return this.payload?.periodLabel ?? "";
		},
		endMonth() {
			return this.payload?.endMonth ?? null;
		},
		getNow() {
			return this.payload?.getNow ?? null;
		},
		categories() {
			return this.payload?.categories ?? [];
		},
	},
	methods: {
		onDateChange(date) {
			this.$emit("update-end-month", date);
		},
		onCategoryChange(id, values) {
			this.$emit("category-change", { id, values });
		},
		// Abrir el select de OTRA categoría ya la vuelve principal, con todos
		// sus ítems tildados: el analista ve el desplegable en el mismo estado
		// en que quedará el análisis, sin tener que tildar un ítem primero.
		// Sobre la principal no se emite nada (abrir no es cambiar).
		onCategoryOpen(cat) {
			if (cat.id === this.category) return;
			this.$emit("category-change", {
				id: cat.id,
				values: (cat.options || []).map((option) => option.value),
			});
		},
	},
};
</script>

<style scoped lang="scss">
// Grid 1fr / auto / 1fr: los selects se anclan al centro real del header y, sin
// categorías, el cluster derecho conserva su columna sin reflow.
.header_profitability_analysis {
	display: grid;
	grid-template-columns: 1fr auto 1fr;
	align-items: center;
	gap: 16px;
}

.title {
	grid-column: 1;
	color: var(--report-text);
	font-size: 28px;
	font-weight: 700;
	letter-spacing: -0.4px;
	margin: 0;
}

.category_filters {
	grid-column: 2;
	gap: 8px;
}

.right_cluster {
	grid-column: 3;
	justify-self: end;
}

.calendar_picker {
	border-radius: 8px;
}

.export_btn {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 6px 14px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
	cursor: pointer;
	height: 46px;

	&[disabled] {
		opacity: 0.55;
		cursor: not-allowed;
	}
}

.export_icon {
	width: 16px;
	height: 16px;
}

.close_btn {
	width: 36px;
	height: 36px;
	border-radius: 8px;
	border: 1px solid var(--report-border-soft);
	background: var(--report-bg);
	box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
	box-sizing: border-box;
	overflow: hidden;
	padding-top: 5px;
	cursor: pointer;

	.ecs {
		width: 100%;
		position: relative;
		height: 20px;
		overflow: hidden;
		flex-shrink: 0;
	}
}
</style>
